import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { getUrl } from "@aws-amplify/storage";
import { generateClient } from "aws-amplify/api";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { PhotoAlbum } from "react-photo-album";
import {
  Lightbox,
  ComponentProps,
  PluginProps,
  createModule,
  MODULE_CONTROLLER,
  useController,
  addToolbarButton,
  IconButton,
  NextIcon,
  PreviousIcon,
  useLightboxState,
  useLightboxProps,
} from "yet-another-react-lightbox";
// import Zoom from "yet-another-react-lightbox/plugins/zoom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme as muiTheme } from "@mui/material/styles";
import NextJsImage from "./NextJsImage2";
import { Loader } from "../Loader";
import { artsByUserIdAndCreatedAtCustom } from "../../src/graphql/custom_queries";
import LikeButton from "./CarouselLikeButton";
import DeleteButton from "./CarouselDeleteButton";
import ShareButton from "./CarouselShareButton";
// import ActionButton from "./CarouselActionButton";
// GraphQL imports
import {
  ModelArtFilterInput,
  ModelBooleanInput,
  ModelSortDirection,
  Art,
} from "../../src/API";
import styles from "./CarouselCreate.module.css";
import FrameButton from "./CarouselFrameButton";

function GalleryComponent() {
  const router = useRouter();

  const handleContextMenu = (event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();
  };

  const handleDragStart = (event: React.DragEvent<HTMLImageElement>) => {
    event.preventDefault();
  };

  const { authStatus, user } = useAuthenticator((context) => [
    context.authStatus,
    context.user,
  ]);

  useEffect(() => {
    if (authStatus === "unauthenticated") {
      router.push("/login");
    }
  }, [authStatus, user, router]);

  const client = generateClient();
  const [imageKeys, setImageKeys] = useState([]);
  const [keys, setKeys] = useState<Art[]>([]);
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noImages, setNoImages] = useState(false);
  const [imgIndex, setImgIndex] = useState<number>(-1);
  const theme = muiTheme(); // Check the screensize
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const childToParent = (childdata: number) => {
    setSlides((prevSlides) =>
      prevSlides.filter((slide, index) => index !== imgIndex),
    );
    setImgIndex(childdata);
  };

  function CarouselWrapper({ children }: ComponentProps) {
    return <div className={styles.yarl__carousel_wrapper}>{children}</div>;
  }

  function CarouselWrapperPlugin({ addParent }: PluginProps) {
    addParent(
      MODULE_CONTROLLER,
      createModule("carousel-wrapper", CarouselWrapper),
    );
  }

  function Frame({ augment }) {
    augment(({ toolbar, ...rest }) => ({
      toolbar: addToolbarButton(
        toolbar,
        "frame",
        <FrameButton filenames={keys.map((art) => art.id.toString())} />,
      ),
      ...rest,
    }));
  }

  function RemoveNavigationPlugin({ remove }: PluginProps) {
    remove("navigation");
  }

  function NavigationButton({ previous }: { previous?: boolean }) {
    const {
      carousel: { finite },
    } = useLightboxProps();
    const { prev, next } = useController();
    const { currentIndex, slides } = useLightboxState();

    return (
      <>
        <IconButton
          icon={previous ? PreviousIcon : NextIcon}
          className="navigationButton"
          label={previous ? "Previous" : "Next"}
          onClick={() => (previous ? prev : next)()}
          disabled={
            slides.length === 0 ||
            (finite &&
              ((previous && currentIndex === 0) ||
                (!previous && currentIndex === slides.length - 1)))
          }
        />
      </>
    );
  }

  function CustomNavigationPlugin({ augment }: PluginProps) {
    augment(({ toolbar, ...rest }) => ({
      toolbar: addToolbarButton(
        addToolbarButton(toolbar, "navigation-next", <NavigationButton />),
        "navigation-previous",
        <NavigationButton previous />,
      ),
      ...rest,
    }));
  }

  function Like({ augment }) {
    augment(({ toolbar, ...rest }) => ({
      toolbar: addToolbarButton(
        toolbar,
        "Like",
        <LikeButton filenames={keys.map((art) => art.id.toString())} />,
      ),
      ...rest,
    }));
  }

  function Delete({ augment }) {
    augment(({ toolbar, ...rest }) => ({
      toolbar: addToolbarButton(
        toolbar,
        "Delete",
        <DeleteButton
          filenames={keys.map((art) => art.id.toString())}
          childToParent={childToParent}
        />,
      ),
      ...rest,
    }));
  }

  function Share({ augment }) {
    augment(({ toolbar, ...rest }) => ({
      toolbar: addToolbarButton(
        toolbar,
        "share",
        <ShareButton
          filenames={keys.map((art) => art.id.toString())}
          identity={keys.map((art) => art.user.identityId)}
        />,
      ),
      ...rest,
    }));
  }

  // const Menu = ({ augment }: PluginProps) => {
  //   augment(({ toolbar, ...rest }) => ({
  //     toolbar: addToolbarButton(toolbar, "Menu", <ActionButton key="Menu" />),
  //     ...rest,
  //   }));
  // };

  useEffect(() => {
    async function fetchImages() {
      try {
        const limit: number = 1000;
        const filterdeleted: ModelBooleanInput = { ne: true };
        const filter: ModelArtFilterInput = {
          deleted: filterdeleted,
        };
        const request = await client.graphql({
          query: artsByUserIdAndCreatedAtCustom,
          variables: {
            userId: user.userId,
            filter,
            limit,
            sortDirection: ModelSortDirection.DESC,
          },
        });
        const arts = request.data.artsByUserIdAndCreatedAt.items as Art[];
        setImageKeys(arts);

        // Set noImages state if there are no images
        setNoImages(arts.length === 0);
      } catch (error) {
        console.error("Error fetching images:", error);
      } finally {
        setIsLoading(false);
      }
    }

    if (imageKeys.length === 0 && user) {
      setIsLoading(true);
      fetchImages();
    }
  }, [client, imageKeys, user]);

  useEffect(() => {
    setKeys(imageKeys);
  }, [imageKeys]);

  useEffect(() => {
    async function fetchPresignedUrls(s3keys) {
      const breakpoints = [1080, 640, 384, 256, 128, 96, 64, 48];
      const getUrlResult = await Promise.all(
        s3keys.map(async (item) => {
          const filename = item.id;
          const urlObject = await getUrl({
            key: filename,
            options: {
              accessLevel: "protected",
            },
          });
          return urlObject.url.href;
        }),
      );
      setSlides(
        getUrlResult.map((photo) => {
          return {
            src: photo,
            width: 1024,
            height: 1024,
            srcSet: breakpoints.map((breakpoint) => {
              const height = Math.round((1024 / 1024) * breakpoint);
              return {
                src: photo,
                width: breakpoint,
                height,
              };
            }),
          };
        }),
      );
      setIsLoading(false);
      return getUrlResult;
    }
    fetchPresignedUrls(keys);
  }, [keys]);

  return (
    <>
      {noImages && (
        <div className="mt-32 flex items-center justify-center">
          No images available.
        </div>
      )}
      {isLoading ? (
        <div className="mt-32 flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
          className="px-16"
        >
          <h1 className="flex justify-center items-center mb-8">
            Your Gallery
          </h1>
          <PhotoAlbum
            layout="rows"
            renderPhoto={NextJsImage}
            photos={slides}
            onClick={({ index: current }) => setImgIndex(current)}
            defaultContainerWidth={1200}
            sizes={{
              size: "calc(100vw - 40px)",
              sizes: [
                { viewport: "(max-width: 299px)", size: "calc(100vw - 10px)" },
                { viewport: "(max-width: 599px)", size: "calc(100vw - 20px)" },
                { viewport: "(max-width: 1199px)", size: "calc(100vw - 30px)" },
              ],
            }}
          />
        </div>
      )}
      <div
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        className="px-2"
      >
        <Lightbox
          className={styles.lightbox}
          index={imgIndex}
          slides={slides}
          open={imgIndex >= 0}
          close={() => setImgIndex(-1)}
          plugins={[
            CarouselWrapperPlugin,
            Delete,
            Share,
            RemoveNavigationPlugin,
            CustomNavigationPlugin,
            Frame,
            Like,
          ]}
          carousel={{ finite: true, padding: 0, spacing: 0 }}
          controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
          // on={{ view: updateIndex }}
          styles={{
            slide: { padding: "0px" },
            root: {
              backgroundColor: "rgba(255, 255, 255, 1)",
              overflow: "hidden",
              "--yarl__color_button": "rgba(0, 0, 0, .8)",
            },
            button: {
              paddingTop: isSmallScreen ? "5px" : "10px",
              paddingBottom: isSmallScreen ? "5px" : "10px",
              paddingRight: isSmallScreen ? "10px" : "15px",
              paddingLeft: isSmallScreen ? "10px" : "15px",
            },
            icon: {
              height: isSmallScreen ? "24px" : "36px",
              width: isSmallScreen ? "24px" : "36px",
            },
          }}
        />
      </div>
    </>
  );
}

export default GalleryComponent;
